import { useTranslation } from "@next-platform/client-common-core";
import type { InputProps } from "@nextui-org/react";
import { Autocomplete, AutocompleteItem, Input } from "@nextui-org/react";
import { forwardRef, HTMLAttributes, SetStateAction, useEffect, useState } from "react";
import { GetCountries, GetState } from "react-country-state-city";

import { cn, getCookie, validateEmail } from "../../utils";
import InputPostalCode from "./InputPostalCode";

export type PersonalInfoProps = HTMLAttributes<HTMLDivElement> & {
  email?: string;
  first_name?: string;
  last_name?: string;
  country?: string;
  state?: string;
  stateOptional?: boolean;
  updatePersonalInfo: (field: string, value: string) => void;
  postal_code?: string;
  variant?: InputProps["variant"];
};

type Country = {
  id: number;
  iso2: string;
  name: string;
};

type State = {
  id: number;
  state_code: string;
  name: string;
};
const getRegionTermFromCountry = (country: string | null) => {
  if (!country) {
    return "Province";
  }
  const countriesWithStates = ["US", "AU", "BR", "IN", "MX", "NG", "DE"];
  return countriesWithStates.includes(country) ? "State" : "Province";
};

const CHECKOUT_GEO_DATA_BASE_URL = "https://venkatvidyut.github.io/react-country-state-city/data";

// For countries without provinces (e.g. Cayman Islands)
const NO_PROVINCE = {
  id: 0,
  name: "NA",
  state_code: "N/A",
};
const countryFromCookie = getCookie("country");
const PersonalInfo = forwardRef<HTMLDivElement, PersonalInfoProps>(
  ({ email, first_name, last_name, country = null, state, stateOptional, postal_code, className, variant, updatePersonalInfo }, ref) => {
    const { t } = useTranslation();
    const [countriesList, setCountriesList] = useState<Country[]>([]);
    const [stateList, setStateList] = useState<State[]>([]);
    const [isEmailInvalid, setIsEmailInvalid] = useState(false);

    useEffect(() => {
      GetCountries(CHECKOUT_GEO_DATA_BASE_URL).then((result: SetStateAction<Country[]>) => {
        setCountriesList(result);
        if (!country && countryFromCookie !== "" && countryFromCookie !== null) {
          updatePersonalInfo("country", countryFromCookie);
        }
      });
    }, []);

    useEffect(() => {
      if (country && countriesList) {
        //we find the country object from the list of countries
        const countryObj = countriesList.find((c) => c.iso2 === country);
        //we set the country object to the state
        GetState(countryObj?.id ?? 0, CHECKOUT_GEO_DATA_BASE_URL).then((result: State[]) => {
          const provinces = result?.length ? result : [NO_PROVINCE];
          setStateList(provinces);
          //if the current state is not in the list of states, we set it to empty
          if (result.length > 0 && !provinces.find((s) => s.state_code === state)) {
            updatePersonalInfo("state", "");
          }
          //if the list of state is empty, we set the state to optional
          if (result.length === 0) {
            updatePersonalInfo("stateOptional", "true");
          } else {
            updatePersonalInfo("stateOptional", "false");
          }
        });
      }
    }, [country, countriesList]);
    return (
      <div ref={ref} className={cn("flex flex-col gap-4", className)}>
        <div className="flex flex-wrap items-center gap-4 xs:flex-nowrap">
          <Input
            isRequired
            label={t("subscription:checkout.personalInfo.firstName")}
            labelPlacement="inside"
            placeholder={t("subscription:checkout.personalInfo.firstNamePlaceholder")}
            variant={variant}
            value={first_name}
            onChange={(e) => updatePersonalInfo("first_name", e.target.value)}
            maxLength={30}
          />
          <Input
            isRequired
            label={t("subscription:checkout.personalInfo.lastName")}
            labelPlacement="inside"
            placeholder={t("subscription:checkout.personalInfo.lastNamePlaceholder")}
            variant={variant}
            value={last_name}
            onChange={(e) => updatePersonalInfo("last_name", e.target.value)}
            maxLength={30}
          />
        </div>

        <div className="flex flex-wrap items-center gap-4 sm:flex-nowrap">
          <Autocomplete
            isRequired
            defaultItems={countriesList}
            selectedKey={country}
            label={t("subscription:checkout.personalInfo.country")}
            labelPlacement="inside"
            placeholder={t("subscription:checkout.personalInfo.countryPlaceholder")}
            showScrollIndicators={false}
            onSelectionChange={(item) => {
              updatePersonalInfo("country", String(item));
            }}
            variant={variant}
          >
            {(item) => (
              <AutocompleteItem key={item?.["iso2"]} value={item?.["iso2"]}>
                {item?.name}
              </AutocompleteItem>
            )}
          </Autocomplete>
        </div>
        <div className="flex flex-wrap items-center gap-4 xs:flex-nowrap">
          <Autocomplete
            isRequired={stateOptional === false}
            isDisabled={country === "" || country === null}
            defaultItems={stateList}
            label={
              getRegionTermFromCountry(country) === "State"
                ? t("subscription:checkout.personalInfo.state")
                : t("subscription:checkout.personalInfo.province")
            }
            labelPlacement="inside"
            placeholder={
              getRegionTermFromCountry(country) === "State"
                ? t("subscription:checkout.personalInfo.statePlaceholder")
                : t("subscription:checkout.personalInfo.provincePlaceholder")
            }
            showScrollIndicators={false}
            onSelectionChange={(item) => {
              updatePersonalInfo("state", String(item));
            }}
            selectedKey={state}
            variant={variant}
          >
            {(item) => (
              <AutocompleteItem key={item?.["state_code"]} value={item?.["state_code"]}>
                {item?.name}
              </AutocompleteItem>
            )}
          </Autocomplete>
          <InputPostalCode postalCode={postal_code ?? ""} countryCode={country ?? ""} updatePersonalInfo={updatePersonalInfo} variant={variant} />
        </div>
        <Input
          isRequired
          value={email}
          label={t("subscription:checkout.personalInfo.email")}
          labelPlacement="inside"
          placeholder={t("subscription:checkout.personalInfo.emailPlaceholder")}
          type="email"
          onChange={(e) => updatePersonalInfo("email", e.target.value)}
          variant={variant}
          maxLength={320}
          onBlur={() => {
            if (email?.length) {
              setIsEmailInvalid(!validateEmail(email));
            }
          }}
          onFocus={() => {
            setIsEmailInvalid(false);
          }}
          isInvalid={isEmailInvalid}
          errorMessage={!!isEmailInvalid && t("subscription:checkout.personalInfo.invalidEmail")}
        />
      </div>
    );
  },
);

PersonalInfo.displayName = "PersonalInfo";

export default PersonalInfo;
